import React, { useState } from "react";
import { Container, Tabs, Tab, Button, Card } from "react-bootstrap";
import MainComponent from "components/MainComponent";
import SubscriptionsList from "./components/Subscriptions/SubscriptionsList";
import CompanySubscriptionDetails from "./components/CompanyDetails/CompanySubscriptionDetails";
import PriceUpdateModal from "./components/PriceManagement/PriceUpdateModal";
import CustomPlanModal from "./components/PriceManagement/CustomPlanModal";
import BlockCompanyModal from "./components/CompanyDetails/BlockCompanyModal";
import PaymentStatusModal from "./components/CompanyDetails/PaymentStatusModal";
import GlobalPriceChangeModal from "./components/PriceManagement/GlobalPriceChangeModal";
import PendingPriceChangesList from "./components/PriceManagement/PendingPriceChangesList";
import PaymentHistoryView from "./components/PaymentHistory/PaymentHistoryView";
import PromotionalCodesList from "./components/PromotionalCodes/PromotionalCodesList";
import PromotionalCodeFormModal from "./components/PromotionalCodes/PromotionalCodeFormModal";
import PromotionalCodeDeleteModal from "./components/PromotionalCodes/PromotionalCodeDeleteModal";
import PromotionalCodeDetailsModal from "./components/PromotionalCodes/PromotionalCodeDetailsModal";
import CompaniesWithoutSubscriptionList from "./components/Subscriptions/CompaniesWithoutSubscriptionList";
import { useUnblockCompany } from "./hooks/useUnblockCompany";
import { useProcessAllPendingPriceChanges } from "./hooks/useProcessAllPendingPriceChanges";
import { usePendingPriceChanges } from "./hooks/usePendingPriceChanges";

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceModalCompanyId, setPriceModalCompanyId] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceModalPlan, setPriceModalPlan] = useState(null);
  const [showCustomPlanModal, setShowCustomPlanModal] = useState(false);
  const [customPlanCompanyId, setCustomPlanCompanyId] = useState(null);
  const [existingCustomPlan, setExistingCustomPlan] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockModalCompanyId, setBlockModalCompanyId] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
  const [paymentStatusCompanyId, setPaymentStatusCompanyId] = useState(null);
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState(null);
  const [showGlobalPriceChangeModal, setShowGlobalPriceChangeModal] = useState(false);
  const [showPromoCodeFormModal, setShowPromoCodeFormModal] = useState(false);
  const [showPromoCodeDeleteModal, setShowPromoCodeDeleteModal] = useState(false);
  const [showPromoCodeDetailsModal, setShowPromoCodeDetailsModal] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [selectedPromoCodeId, setSelectedPromoCodeId] = useState(null);
  const [previousTab, setPreviousTab] = useState("list");

  const { mutate: unblockCompany } = useUnblockCompany();
  const { mutate: processAllPending, isPending: isProcessingAll } = useProcessAllPendingPriceChanges();
  const { data: pendingPriceChanges } = usePendingPriceChanges({ status: "pending" });
  const hasPendingChanges = pendingPriceChanges && pendingPriceChanges.length > 0;

  const handleViewDetails = (companyId, sourceTab = "list") => {
    setPreviousTab(sourceTab);
    setSelectedCompanyId(companyId);
    setActiveTab("details");
  };

  const handleBackToList = () => {
    setSelectedCompanyId(null);
    setActiveTab(previousTab);
  };

  const handleUpdatePrice = (companyId, currentPriceValue, plan = null) => {
    setPriceModalCompanyId(companyId);
    setCurrentPrice(currentPriceValue);
    setPriceModalPlan(plan);
    setShowPriceModal(true);
  };

  const handleCreateCustomPlan = (companyId) => {
    setCustomPlanCompanyId(companyId);
    setExistingCustomPlan(null);
    setShowCustomPlanModal(true);
  };

  const handleEditCustomPlan = (companyId, plan) => {
    setCustomPlanCompanyId(companyId);
    setExistingCustomPlan(plan);
    setShowCustomPlanModal(true);
  };

  const handleBlockCompany = (companyId, blocked) => {
    setBlockModalCompanyId(companyId);
    setIsBlocked(blocked);
    setShowBlockModal(true);
  };

  const handleUnblock = (companyId) => {
    unblockCompany(companyId, {
      onSuccess: () => {
        setShowBlockModal(false);
      },
    });
  };

  const handleUpdatePaymentStatus = (companyId, currentStatus) => {
    setPaymentStatusCompanyId(companyId);
    setCurrentPaymentStatus(currentStatus);
    setShowPaymentStatusModal(true);
  };

  const handleProcessAllPending = () => {
    if (window.confirm("Are you sure you want to process all pending price changes? This will apply all changes that are due.")) {
      processAllPending();
    }
  };

  const handleCreatePromoCode = () => {
    setSelectedPromoCode(null);
    setShowPromoCodeFormModal(true);
  };

  const handleEditPromoCode = (code) => {
    setSelectedPromoCode(code);
    setShowPromoCodeFormModal(true);
  };

  const handleDeletePromoCode = (code) => {
    setSelectedPromoCode(code);
    setShowPromoCodeDeleteModal(true);
  };

  const handleViewPromoCodeDetails = (codeId) => {
    setSelectedPromoCodeId(codeId);
    setShowPromoCodeDetailsModal(true);
  };

  return (
    <MainComponent>
      <Container fluid>
        <div style={{ marginBottom: "2rem", paddingTop: "20px" }}>
          <h2 style={{ fontSize: "1.875rem", fontWeight: 700, margin: 0, color: "#212529" }}>
            Subscription Management
          </h2>
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="list" title="Subscriptions">
            {activeTab === "list" && (
              <SubscriptionsList
                onViewDetails={handleViewDetails}
                onUpdatePrice={handleUpdatePrice}
                onBlockCompany={handleBlockCompany}
                onUpdatePaymentStatus={handleUpdatePaymentStatus}
              />
            )}
          </Tab>

          <Tab eventKey="details" title="Company Details" disabled={!selectedCompanyId}>
            {activeTab === "details" && selectedCompanyId && (
              <div>
                <CompanySubscriptionDetails
                  companyId={selectedCompanyId}
                  onBack={handleBackToList}
                  onUpdatePrice={handleUpdatePrice}
                  onCreateCustomPlan={handleCreateCustomPlan}
                  onEditCustomPlan={handleEditCustomPlan}
                  onBlockCompany={handleBlockCompany}
                  onUpdatePaymentStatus={handleUpdatePaymentStatus}
                />
             
              </div>
            )}
          </Tab>

          <Tab eventKey="global" title="Global Price Changes">
            {activeTab === "global" && (
              <>
                <Card className="mb-3">
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5>Global Price Change Management</h5>
                      <div>
                        <Button
                          variant="primary"
                          className="me-2"
                          onClick={() => setShowGlobalPriceChangeModal(true)}
                          disabled={hasPendingChanges}
                          title={hasPendingChanges ? "Please process or cancel pending price changes before initiating a new one" : ""}
                        >
                          Initiate Price Change
                        </Button>
                        <Button
                          variant="success"
                          onClick={handleProcessAllPending}
                          disabled={isProcessingAll}
                        >
                          {isProcessingAll ? "Processing..." : "Process All Pending"}
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                </Card>
                <PendingPriceChangesList />
              </>
            )}
          </Tab>

          <Tab eventKey="promotional-codes" title="Promotional Codes">
            {activeTab === "promotional-codes" && (
              <PromotionalCodesList
                onCreate={handleCreatePromoCode}
                onEdit={handleEditPromoCode}
                onDelete={handleDeletePromoCode}
                onViewDetails={handleViewPromoCodeDetails}
              />
            )}
          </Tab>

          <Tab eventKey="pending-subscriptions" title="Pending Subscriptions">
            {activeTab === "pending-subscriptions" && (
              <CompaniesWithoutSubscriptionList onViewDetails={handleViewDetails} />
            )}
          </Tab>
        </Tabs>

        {/* Modals */}
        <PriceUpdateModal
          show={showPriceModal}
          onHide={() => {
            setShowPriceModal(false);
            setPriceModalCompanyId(null);
            setCurrentPrice(null);
            setPriceModalPlan(null);
          }}
          companyId={priceModalCompanyId}
          currentPrice={currentPrice}
          plan={priceModalPlan}
        />

        <CustomPlanModal
          show={showCustomPlanModal}
          onHide={() => {
            setShowCustomPlanModal(false);
            setCustomPlanCompanyId(null);
            setExistingCustomPlan(null);
          }}
          companyId={customPlanCompanyId}
          existingPlan={existingCustomPlan}
        />

        <BlockCompanyModal
          show={showBlockModal}
          onHide={() => {
            setShowBlockModal(false);
            setBlockModalCompanyId(null);
            setIsBlocked(false);
          }}
          companyId={blockModalCompanyId}
          isBlocked={isBlocked}
          onUnblock={handleUnblock}
        />

        <PaymentStatusModal
          show={showPaymentStatusModal}
          onHide={() => {
            setShowPaymentStatusModal(false);
            setPaymentStatusCompanyId(null);
            setCurrentPaymentStatus(null);
          }}
          companyId={paymentStatusCompanyId}
          currentStatus={currentPaymentStatus}
        />

        <GlobalPriceChangeModal
          show={showGlobalPriceChangeModal}
          onHide={() => setShowGlobalPriceChangeModal(false)}
        />

        <PromotionalCodeFormModal
          show={showPromoCodeFormModal}
          onHide={() => {
            setShowPromoCodeFormModal(false);
            setSelectedPromoCode(null);
          }}
          existingCode={selectedPromoCode}
        />

        <PromotionalCodeDeleteModal
          show={showPromoCodeDeleteModal}
          onHide={() => {
            setShowPromoCodeDeleteModal(false);
            setSelectedPromoCode(null);
          }}
          code={selectedPromoCode}
        />

        <PromotionalCodeDetailsModal
          show={showPromoCodeDetailsModal}
          onHide={() => {
            setShowPromoCodeDetailsModal(false);
            setSelectedPromoCodeId(null);
          }}
          codeId={selectedPromoCodeId}
        />
      </Container>
    </MainComponent>
  );
};

export default SubscriptionManagement;

